import { useState, useMemo } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useExpenses } from "@/contexts/ExpenseContext";
import { Ledger, LedgerGroup, GROUP_NATURE } from "@/lib/accounting/types";
import { exportToCSV, exportToExcel, exportToPDF, formatCurrency, ReportData } from "@/lib/reports/exportUtils";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  FileText, Download, FileSpreadsheet, File, Scale, TrendingUp,
  BarChart3, BookOpen, Wallet, Package, Receipt, ChevronRight, ChevronDown,
  Calendar, Landmark, CheckCircle2, ArrowRightLeft, CreditCard, IndianRupee
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type ReportType =
  | "balance-sheet"
  | "profit-loss"
  | "trial-balance"
  | "day-book"
  | "cash-book"
  | "bank-book"
  | "ledger"
  | "cash-flow"
  | "inventory"
  | "gst";

const REPORT_TABS: { id: ReportType; label: string; icon: any }[] = [
  { id: "balance-sheet", label: "Balance Sheet", icon: Scale },
  { id: "profit-loss", label: "Profit & Loss", icon: TrendingUp },
  { id: "trial-balance", label: "Trial Balance", icon: BarChart3 },
  { id: "day-book", label: "Day Book", icon: Calendar },
  { id: "cash-book", label: "Cash Book", icon: Wallet },
  { id: "bank-book", label: "Bank Book", icon: Landmark },
  { id: "ledger", label: "Ledger Reports", icon: BookOpen },
  { id: "cash-flow", label: "Cash Flow", icon: TrendingUp },
  { id: "inventory", label: "Inventory", icon: Package },
  { id: "gst", label: "GST Summary", icon: Receipt },
];

const ASSET_GROUPS: LedgerGroup[] = ["Cash-in-Hand", "Bank Accounts", "Sundry Debtors", "Stock-in-Hand", "Current Assets", "Fixed Assets", "Investments"];
const LIABILITY_GROUPS: LedgerGroup[] = ["Sundry Creditors", "Current Liabilities", "Loans (Liability)", "Duties & Taxes", "Provisions", "Secured Loans", "Unsecured Loans"];
const EQUITY_GROUPS: LedgerGroup[] = ["Capital Account", "Reserves & Surplus"];
const INCOME_GROUPS: LedgerGroup[] = ["Sales Accounts", "Direct Income", "Indirect Income", "Revenue"];
const EXPENSE_GROUPS: LedgerGroup[] = ["Purchase Accounts", "Direct Expenses", "Indirect Expenses"];

const ReportsPage = () => {
  const { ledgers, vouchers, stockItems } = useAccounting();
  const { expenses } = useExpenses();
  const { toast } = useToast();
  const today = new Date().toISOString().split("T")[0];
  const [asOfDate, setAsOfDate] = useState(today);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    const month = d.getMonth() + 1;
    const year = month >= 4 ? d.getFullYear() : d.getFullYear() - 1;
    return `${year}-04-01`;
  });
  const [toDate, setToDate] = useState(today);
  const [selectedBankLedgerId, setSelectedBankLedgerId] = useState<string>("all");

  const [activeTab, setActiveTab] = useState<ReportType>("balance-sheet");
  const [selectedLedger, setSelectedLedger] = useState<string>("");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const reportDate = new Date(asOfDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  const toggleGroup = (group: string) => {
    const newSet = new Set(expandedGroups);
    if (newSet.has(group)) newSet.delete(group);
    else newSet.add(group);
    setExpandedGroups(newSet);
  };

  // Dynamically compute ledger balance as of asOfDate (incorporating backdated transactions)
  const effectiveLedgers = useMemo(() => {
    return ledgers.map((l) => {
      let signedBal = (l.openingBalance || 0) * (l.openingBalanceType === "Dr" ? 1 : -1);
      for (const v of vouchers) {
        if (v.status !== "Approved" || v.date > asOfDate) continue;
        for (const line of v.lines) {
          if (line.ledgerId === l.id) {
            signedBal += (line.debit || 0) - (line.credit || 0);
          }
        }
      }
      return {
        ...l,
        currentBalance: Math.abs(signedBal),
        currentBalanceType: signedBal >= 0 ? ("Dr" as const) : ("Cr" as const),
      };
    });
  }, [ledgers, vouchers, asOfDate]);

  // =====================
  // BALANCE SHEET DATA (As of asOfDate)
  // =====================
  const balanceSheet = useMemo(() => {
    const groupLedgers = (groups: LedgerGroup[]) => {
      const result: { group: LedgerGroup; ledgers: Ledger[]; total: number }[] = [];
      for (const g of groups) {
        const leds = effectiveLedgers.filter((l) => l.group === g);
        if (leds.length === 0) continue;
        const total = leds.reduce((sum, l) => {
          const val = l.currentBalance * (l.currentBalanceType === "Dr" ? 1 : -1);
          return sum + (GROUP_NATURE[g] === "Asset" ? val : -val);
        }, 0);
        result.push({ group: g, ledgers: leds, total });
      }
      return result;
    };

    const assets = groupLedgers(ASSET_GROUPS);
    const liabilities = groupLedgers(LIABILITY_GROUPS);
    const equity = groupLedgers(EQUITY_GROUPS);

    const totalAssets = assets.reduce((s, g) => s + g.total, 0);
    const totalLiabilities = liabilities.reduce((s, g) => s + g.total, 0);
    const totalEquity = equity.reduce((s, g) => s + g.total, 0);

    // Calculate retained earnings (P&L as of asOfDate)
    const incomeTotal = effectiveLedgers
      .filter((l) => INCOME_GROUPS.includes(l.group as LedgerGroup))
      .reduce((s, l) => s + l.currentBalance * (l.currentBalanceType === "Cr" ? 1 : -1), 0);
    const expenseTotal = effectiveLedgers
      .filter((l) => EXPENSE_GROUPS.includes(l.group as LedgerGroup))
      .reduce((s, l) => s + l.currentBalance * (l.currentBalanceType === "Dr" ? 1 : -1), 0);
    const expModuleAsOf = expenses
      .filter((e) => e.status === "Approved" && e.date <= asOfDate)
      .reduce((s, e) => s + Number(e.amount || 0), 0);
    const retainedEarnings = incomeTotal - expenseTotal - expModuleAsOf;

    return { assets, liabilities, equity, totalAssets, totalLiabilities, totalEquity, retainedEarnings };
  }, [effectiveLedgers, expenses, asOfDate]);

  // =====================
  // PROFIT & LOSS DATA (Between fromDate and toDate)
  // =====================
  const profitLoss = useMemo(() => {
    let sales = 0;
    let otherIncome = 0;
    let purchases = 0;
    let directExpenses = 0;
    let indirectExpenses = 0;

    for (const v of vouchers) {
      if (v.status !== "Approved" || v.date < fromDate || v.date > toDate) continue;
      for (const line of v.lines) {
        const ledger = ledgers.find((l) => l.id === line.ledgerId);
        if (!ledger) continue;
        const netCredit = (line.credit || 0) - (line.debit || 0);
        const netDebit = (line.debit || 0) - (line.credit || 0);

        if (ledger.group === "Sales Accounts") sales += netCredit;
        else if (["Direct Income", "Indirect Income", "Revenue"].includes(ledger.group)) otherIncome += netCredit;
        else if (ledger.group === "Purchase Accounts") purchases += netDebit;
        else if (ledger.group === "Direct Expenses") directExpenses += netDebit;
        else if (ledger.group === "Indirect Expenses") indirectExpenses += netDebit;
      }
    }

    const expModulePeriod = expenses
      .filter((e) => e.status === "Approved" && e.date >= fromDate && e.date <= toDate)
      .reduce((s, e) => s + Number(e.amount || 0), 0);
    indirectExpenses += expModulePeriod;

    const grossProfit = sales - purchases - directExpenses;
    const netProfit = grossProfit + otherIncome - indirectExpenses;

    return { sales, otherIncome, purchases, directExpenses, indirectExpenses, grossProfit, netProfit };
  }, [ledgers, vouchers, expenses, fromDate, toDate]);

  // =====================
  // TRIAL BALANCE DATA (As of asOfDate)
  // =====================
  const trialBalance = useMemo(() => {
    const rows = effectiveLedgers
      .filter((l) => l.currentBalance !== 0)
      .map((l) => ({
        name: l.name,
        group: l.group,
        debit: l.currentBalanceType === "Dr" ? l.currentBalance : 0,
        credit: l.currentBalanceType === "Cr" ? l.currentBalance : 0,
      }))
      .sort((a, b) => a.group.localeCompare(b.group));

    const totalDebit = rows.reduce((s, r) => s + r.debit, 0);
    const totalCredit = rows.reduce((s, r) => s + r.credit, 0);

    return { rows, totalDebit, totalCredit, isBalanced: Math.abs(totalDebit - totalCredit) < 0.01 };
  }, [effectiveLedgers]);

  // =====================
  // DAY BOOK DATA (Between fromDate and toDate)
  // =====================
  const dayBookVouchers = useMemo(() => {
    return vouchers
      .filter((v) => v.status === "Approved" && v.date >= fromDate && v.date <= toDate)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [vouchers, fromDate, toDate]);

  // =====================
  // CASH BOOK DATA (Between fromDate and toDate)
  // =====================
  const cashBookData = useMemo(() => {
    const cashLedgers = ledgers.filter((l) => l.group === "Cash-in-Hand");
    const cashLedgerIds = new Set(cashLedgers.map((l) => l.id));

    let opening = 0;
    for (const l of cashLedgers) {
      opening += (l.openingBalance || 0) * (l.openingBalanceType === "Dr" ? 1 : -1);
    }
    for (const v of vouchers) {
      if (v.status !== "Approved" || v.date >= fromDate) continue;
      for (const line of v.lines) {
        if (cashLedgerIds.has(line.ledgerId)) {
          opening += (line.debit || 0) - (line.credit || 0);
        }
      }
    }

    let running = opening;
    const entries: {
      date: string;
      voucherNumber: string;
      type: string;
      particulars: string;
      debit: number;
      credit: number;
      runningBalance: number;
    }[] = [];

    const periodVouchers = vouchers
      .filter((v) => v.status === "Approved" && v.date >= fromDate && v.date <= toDate)
      .sort((a, b) => a.date.localeCompare(b.date));

    for (const v of periodVouchers) {
      const cashLines = v.lines.filter((l) => cashLedgerIds.has(l.ledgerId));
      if (cashLines.length === 0) continue;
      const otherLines = v.lines.filter((l) => !cashLedgerIds.has(l.ledgerId));
      const particulars =
        otherLines.map((l) => l.ledgerName).join(", ") || v.partyName || v.narration || "Cash Entry";

      const debit = cashLines.reduce((s, l) => s + l.debit, 0);
      const credit = cashLines.reduce((s, l) => s + l.credit, 0);
      running += debit - credit;

      entries.push({
        date: v.date,
        voucherNumber: v.voucherNumber,
        type: v.type,
        particulars,
        debit,
        credit,
        runningBalance: running,
      });
    }

    return {
      openingBalance: opening,
      entries,
      totalDebit: entries.reduce((s, e) => s + e.debit, 0),
      totalCredit: entries.reduce((s, e) => s + e.credit, 0),
      closingBalance: running,
    };
  }, [ledgers, vouchers, fromDate, toDate]);

  // =====================
  // BANK BOOK DATA (Between fromDate and toDate)
  // =====================
  const bankBookData = useMemo(() => {
    const allBankLedgers = ledgers.filter((l) => l.group === "Bank Accounts");
    const targetBanks =
      selectedBankLedgerId === "all"
        ? allBankLedgers
        : allBankLedgers.filter((l) => l.id === selectedBankLedgerId);
    const bankIds = new Set(targetBanks.map((l) => l.id));

    let opening = 0;
    for (const l of targetBanks) {
      opening += (l.openingBalance || 0) * (l.openingBalanceType === "Dr" ? 1 : -1);
    }
    for (const v of vouchers) {
      if (v.status !== "Approved" || v.date >= fromDate) continue;
      for (const line of v.lines) {
        if (bankIds.has(line.ledgerId)) {
          opening += (line.debit || 0) - (line.credit || 0);
        }
      }
    }

    let running = opening;
    const entries: {
      date: string;
      voucherNumber: string;
      type: string;
      particulars: string;
      instrumentRef?: string;
      debit: number;
      credit: number;
      runningBalance: number;
    }[] = [];

    const periodVouchers = vouchers
      .filter((v) => v.status === "Approved" && v.date >= fromDate && v.date <= toDate)
      .sort((a, b) => a.date.localeCompare(b.date));

    for (const v of periodVouchers) {
      const bankLines = v.lines.filter((l) => bankIds.has(l.ledgerId));
      if (bankLines.length === 0) continue;
      const otherLines = v.lines.filter((l) => !bankIds.has(l.ledgerId));
      const particulars =
        otherLines.map((l) => l.ledgerName).join(", ") || v.partyName || v.narration || "Bank Entry";

      const debit = bankLines.reduce((s, l) => s + l.debit, 0);
      const credit = bankLines.reduce((s, l) => s + l.credit, 0);
      running += debit - credit;

      entries.push({
        date: v.date,
        voucherNumber: v.voucherNumber,
        type: v.type,
        particulars,
        instrumentRef: v.instrumentNo || v.paymentMode,
        debit,
        credit,
        runningBalance: running,
      });
    }

    return {
      banks: allBankLedgers,
      openingBalance: opening,
      entries,
      totalDebit: entries.reduce((s, e) => s + e.debit, 0),
      totalCredit: entries.reduce((s, e) => s + e.credit, 0),
      closingBalance: running,
    };
  }, [ledgers, vouchers, fromDate, toDate, selectedBankLedgerId]);

  // =====================
  // CASH FLOW DATA
  // =====================
  const cashFlow = useMemo(() => {
    const cashBankLedgers = ledgers.filter((l) => l.group === "Cash-in-Hand" || l.group === "Bank Accounts");
    const openingBalance = cashBankLedgers.reduce((s, l) => s + l.openingBalance, 0);
    const currentBalance = cashBankLedgers.reduce((s, l) => s + l.currentBalance, 0);

    const cashVouchers = vouchers.filter((v) =>
      v.status === "Approved" &&
      v.lines.some((line) => cashBankLedgers.some((cl) => cl.id === line.ledgerId))
    );

    const operating = cashVouchers
      .filter((v) => ["Sales", "Purchase", "Receipt", "Payment"].includes(v.type))
      .reduce((s, v) => {
        const cashLine = v.lines.find((l) => cashBankLedgers.some((cl) => cl.id === l.ledgerId));
        if (!cashLine) return s;
        return s + (cashLine.debit - cashLine.credit);
      }, 0);

    const investing = 0; // Fixed asset purchases would go here
    const financing = 0; // Capital/loan changes would go here

    return {
      openingBalance,
      currentBalance,
      operating,
      investing,
      financing,
      netChange: currentBalance - openingBalance,
      ledgers: cashBankLedgers,
    };
  }, [ledgers, vouchers]);

  // =====================
  // GST SUMMARY DATA
  // =====================
  const gstSummary = useMemo(() => {
    const salesVouchers = vouchers.filter((v) => v.type === "Sales" && v.status === "Approved");
    const purchaseVouchers = vouchers.filter((v) => v.type === "Purchase" && v.status === "Approved");

    const outputCGST = salesVouchers.reduce((s, v) => s + (v.cgst || 0), 0);
    const outputSGST = salesVouchers.reduce((s, v) => s + (v.sgst || 0), 0);
    const outputIGST = salesVouchers.reduce((s, v) => s + (v.igst || 0), 0);
    const inputCGST = purchaseVouchers.reduce((s, v) => s + (v.cgst || 0), 0);
    const inputSGST = purchaseVouchers.reduce((s, v) => s + (v.sgst || 0), 0);
    const inputIGST = purchaseVouchers.reduce((s, v) => s + (v.igst || 0), 0);

    const totalOutput = outputCGST + outputSGST + outputIGST;
    const totalInput = inputCGST + inputSGST + inputIGST;
    const netPayable = totalOutput - totalInput;

    const taxableSupply = salesVouchers.reduce((s, v) => s + v.totalDebit - (v.gstAmount || 0), 0);
    const taxablePurchase = purchaseVouchers.reduce((s, v) => s + v.totalDebit - (v.gstAmount || 0), 0);

    return {
      outputCGST, outputSGST, outputIGST, totalOutput,
      inputCGST, inputSGST, inputIGST, totalInput,
      netPayable, taxableSupply, taxablePurchase,
      salesCount: salesVouchers.length,
      purchaseCount: purchaseVouchers.length,
    };
  }, [vouchers]);

  // =====================
  // LEDGER REPORT DATA
  // =====================
  const ledgerReport = useMemo(() => {
    if (!selectedLedger) return null;
    const ledger = ledgers.find((l) => l.id === selectedLedger);
    if (!ledger) return null;

    const transactions = vouchers
      .filter((v) => v.status === "Approved" && v.lines.some((l) => l.ledgerId === selectedLedger))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map((v) => {
        const line = v.lines.find((l) => l.ledgerId === selectedLedger)!;
        return { date: v.date, voucherNumber: v.voucherNumber, narration: v.narration, debit: line.debit, credit: line.credit };
      });

    return { ledger, transactions };
  }, [selectedLedger, ledgers, vouchers]);

  // =====================
  // INVENTORY REPORT DATA
  // =====================
  const inventoryReport = useMemo(() => {
    const totalValue = stockItems.reduce((s, i) => s + i.currentValue, 0);
    const totalItems = stockItems.length;
    const lowStock = stockItems.filter((i) => i.reorderLevel && i.currentQty <= i.reorderLevel);

    return { items: stockItems, totalValue, totalItems, lowStockCount: lowStock.length };
  }, [stockItems]);

  // =====================
  // EXPORT HANDLERS
  // =====================
  const handleExport = (format: "csv" | "excel" | "pdf") => {
    let data: ReportData | null = null;
    let filename = "";

    switch (activeTab) {
      case "balance-sheet":
        data = {
          title: "Balance Sheet",
          subtitle: "As on " + reportDate,
          date: reportDate,
          headers: ["Particulars", "Amount (₹)"],
          rows: [
            ["ASSETS", ""],
            ...balanceSheet.assets.flatMap((g) => [
              [g.group, formatCurrency(g.total)],
              ...g.ledgers.map((l) => [`  ${l.name}`, formatCurrency(l.currentBalance)]),
            ]),
            ["Total Assets", formatCurrency(balanceSheet.totalAssets)],
            ["", ""],
            ["LIABILITIES", ""],
            ...balanceSheet.liabilities.flatMap((g) => [
              [g.group, formatCurrency(g.total)],
            ]),
            ["Total Liabilities", formatCurrency(balanceSheet.totalLiabilities)],
            ["", ""],
            ["EQUITY", ""],
            ...balanceSheet.equity.flatMap((g) => [[g.group, formatCurrency(g.total)]]),
            ["Retained Earnings", formatCurrency(balanceSheet.retainedEarnings)],
            ["Total Equity", formatCurrency(balanceSheet.totalEquity + balanceSheet.retainedEarnings)],
          ],
        };
        filename = "balance-sheet";
        break;

      case "profit-loss":
        data = {
          title: "Profit & Loss Statement",
          subtitle: `For the period ending ${reportDate}`,
          date: reportDate,
          headers: ["Particulars", "Amount (₹)"],
          rows: [
            ["Sales Revenue", formatCurrency(profitLoss.sales)],
            ["Less: Cost of Goods Sold", ""],
            ["  Purchases", formatCurrency(profitLoss.purchases)],
            ["  Direct Expenses", formatCurrency(profitLoss.directExpenses)],
            ["Gross Profit", formatCurrency(profitLoss.grossProfit)],
            ["", ""],
            ["Add: Other Income", formatCurrency(profitLoss.otherIncome)],
            ["Less: Indirect Expenses", formatCurrency(profitLoss.indirectExpenses)],
            ["", ""],
            ["Net Profit / (Loss)", formatCurrency(profitLoss.netProfit)],
          ],
        };
        filename = "profit-loss";
        break;

      case "trial-balance":
        data = {
          title: "Trial Balance",
          subtitle: `As on ${reportDate}`,
          date: reportDate,
          headers: ["Ledger Name", "Group", "Debit (₹)", "Credit (₹)"],
          rows: trialBalance.rows.map((r) => [r.name, r.group, r.debit ? formatCurrency(r.debit) : "", r.credit ? formatCurrency(r.credit) : ""]),
          totals: ["Total", "", formatCurrency(trialBalance.totalDebit), formatCurrency(trialBalance.totalCredit)],
        };
        filename = "trial-balance";
        break;

      case "cash-flow":
        data = {
          title: "Cash Flow Statement",
          subtitle: `For the period ending ${reportDate}`,
          date: reportDate,
          headers: ["Particulars", "Amount (₹)"],
          rows: [
            ["Opening Cash & Bank Balance", formatCurrency(cashFlow.openingBalance)],
            ["", ""],
            ["Cash Flow from Operating Activities", formatCurrency(cashFlow.operating)],
            ["Cash Flow from Investing Activities", formatCurrency(cashFlow.investing)],
            ["Cash Flow from Financing Activities", formatCurrency(cashFlow.financing)],
            ["", ""],
            ["Net Change in Cash", formatCurrency(cashFlow.netChange)],
            ["Closing Cash & Bank Balance", formatCurrency(cashFlow.currentBalance)],
          ],
        };
        filename = "cash-flow";
        break;

      case "inventory":
        data = {
          title: "Inventory Valuation Report",
          subtitle: `As on ${reportDate}`,
          date: reportDate,
          headers: ["Item Name", "HSN", "Qty", "Unit", "Rate (₹)", "Value (₹)"],
          rows: inventoryReport.items.map((i) => [i.name, i.hsnCode, i.currentQty, i.unit, formatCurrency(i.currentRate), formatCurrency(i.currentValue)]),
          totals: ["Total", "", "", "", "", formatCurrency(inventoryReport.totalValue)],
        };
        filename = "inventory-valuation";
        break;

      case "gst":
        data = {
          title: "GST Summary Report",
          subtitle: `For the period ending ${reportDate}`,
          date: reportDate,
          headers: ["Particulars", "CGST (₹)", "SGST (₹)", "IGST (₹)", "Total (₹)"],
          rows: [
            ["Output Tax (Sales)", formatCurrency(gstSummary.outputCGST), formatCurrency(gstSummary.outputSGST), formatCurrency(gstSummary.outputIGST), formatCurrency(gstSummary.totalOutput)],
            ["Input Tax (Purchases)", formatCurrency(gstSummary.inputCGST), formatCurrency(gstSummary.inputSGST), formatCurrency(gstSummary.inputIGST), formatCurrency(gstSummary.totalInput)],
            ["", "", "", "", ""],
            ["Net Tax Payable / (Refundable)", "", "", "", formatCurrency(gstSummary.netPayable)],
          ],
        };
        filename = "gst-summary";
        break;

      case "ledger":
        if (!ledgerReport) {
          toast({ title: "Select a Ledger", description: "Please select a ledger to export", variant: "destructive" });
          return;
        }
        data = {
          title: `Ledger Account: ${ledgerReport.ledger.name}`,
          subtitle: `As on ${reportDate}`,
          date: reportDate,
          headers: ["Date", "Voucher No", "Narration", "Debit (₹)", "Credit (₹)"],
          rows: ledgerReport.transactions.map((t) => [
            new Date(t.date).toLocaleDateString("en-IN"),
            t.voucherNumber,
            t.narration.slice(0, 50),
            t.debit ? formatCurrency(t.debit) : "",
            t.credit ? formatCurrency(t.credit) : "",
          ]),
        };
        filename = `ledger-${ledgerReport.ledger.name.toLowerCase().replace(/\s+/g, "-")}`;
        break;
    }

    if (!data) return;

    switch (format) {
      case "csv":
        exportToCSV(data, filename);
        break;
      case "excel":
        exportToExcel(data, filename);
        break;
      case "pdf":
        exportToPDF(data, filename);
        break;
    }

    toast({ title: "Exported", description: `${data.title} exported as ${format.toUpperCase()}` });
  };

  // =====================
  // RENDER GROUP SECTION
  // =====================
  const renderGroupSection = (title: string, groups: { group: LedgerGroup; ledgers: Ledger[]; total: number }[], totalLabel: string, total: number) => (
    <div className="mb-6">
      <h3 className="text-sm font-bold text-accent uppercase tracking-wider mb-3">{title}</h3>
      {groups.map((g) => (
        <div key={g.group} className="mb-2">
          <button
            onClick={() => toggleGroup(g.group)}
            className="w-full flex items-center justify-between py-2 px-3 bg-muted/50 rounded-md hover:bg-muted transition-colors text-sm"
          >
            <span className="flex items-center gap-2">
              {expandedGroups.has(g.group) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              <span className="font-medium text-foreground">{g.group}</span>
              <span className="text-xs text-muted-foreground">({g.ledgers.length})</span>
            </span>
            <span className="font-semibold text-foreground">{formatCurrency(g.total)}</span>
          </button>
          {expandedGroups.has(g.group) && (
            <div className="ml-6 mt-1 space-y-1">
              {g.ledgers.map((l) => (
                <div key={l.id} className="flex justify-between py-1.5 px-2 text-sm text-muted-foreground border-l-2 border-border">
                  <span>{l.name}</span>
                  <span>{formatCurrency(l.currentBalance)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="flex justify-between py-2 px-3 bg-accent/10 rounded-md mt-3 text-sm font-bold text-accent">
        <span>{totalLabel}</span>
        <span>{formatCurrency(total)}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-display text-foreground">Financial Reports</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Generate, view, and export professional financial statements</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport("csv")} className="gap-1.5 flex-1 sm:flex-none">
            <FileText className="h-3.5 w-3.5" /> <span className="hidden sm:inline">CSV</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport("excel")} className="gap-1.5 flex-1 sm:flex-none">
            <FileSpreadsheet className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Excel</span>
          </Button>
          <Button size="sm" onClick={() => handleExport("pdf")} className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90 flex-1 sm:flex-none">
            <File className="h-3.5 w-3.5" /> PDF
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ReportType)}>
        <div className="scrollable-tabs">
          <TabsList className="bg-muted/50 flex-nowrap h-auto p-1 w-max sm:w-auto">
            {REPORT_TABS.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id} className="text-xs sm:text-sm gap-1 sm:gap-1.5 whitespace-nowrap px-2.5 sm:px-3">
                <tab.icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(" ")[0]}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* Date Filtering Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-card border border-border mt-3 text-xs">
          {activeTab === "balance-sheet" || activeTab === "trial-balance" ? (
            <div className="flex items-center gap-2">
              <span className="font-semibold text-muted-foreground uppercase tracking-wider text-[11px]">As on Date:</span>
              <input
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
                className="h-8 px-2.5 rounded border border-border bg-muted/40 font-mono text-xs text-foreground"
              />
              <span className="text-[11px] text-muted-foreground hidden sm:inline">
                (Includes all historical & backdated vouchers up to this date)
              </span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-muted-foreground uppercase tracking-wider text-[11px]">Reporting Period:</span>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground text-[11px]">From:</span>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="h-8 px-2 rounded border border-border bg-muted/40 font-mono text-xs text-foreground"
                />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground text-[11px]">To:</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="h-8 px-2 rounded border border-border bg-muted/40 font-mono text-xs text-foreground"
                />
              </div>

              {/* Quick Period Presets */}
              <div className="flex items-center gap-1 pl-2 border-l border-border">
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    const month = d.getMonth() + 1;
                    const year = month >= 4 ? d.getFullYear() : d.getFullYear() - 1;
                    setFromDate(`${year}-04-01`);
                    setToDate(today);
                  }}
                  className="px-2 py-0.5 rounded bg-muted hover:bg-muted/80 text-[10px] font-medium"
                >
                  Current FY
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, "0");
                    setFromDate(`${y}-${m}-01`);
                    setToDate(today);
                  }}
                  className="px-2 py-0.5 rounded bg-muted hover:bg-muted/80 text-[10px] font-medium"
                >
                  This Month
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    d.setDate(d.getDate() - 30);
                    setFromDate(d.toISOString().split("T")[0]);
                    setToDate(today);
                  }}
                  className="px-2 py-0.5 rounded bg-muted hover:bg-muted/80 text-[10px] font-medium"
                >
                  Last 30 Days
                </button>
              </div>
            </div>
          )}

          {activeTab === "bank-book" && (
            <div className="flex items-center gap-2">
              <span className="font-semibold text-muted-foreground text-[11px]">Bank Account:</span>
              <Select value={selectedBankLedgerId} onValueChange={setSelectedBankLedgerId}>
                <SelectTrigger className="h-8 text-xs min-w-[160px]">
                  <SelectValue placeholder="All Banks" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">
                    All Bank Accounts ({bankBookData.banks.length})
                  </SelectItem>
                  {bankBookData.banks.map((b) => (
                    <SelectItem key={b.id} value={b.id} className="text-xs">
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* BALANCE SHEET */}
        <TabsContent value="balance-sheet" className="mt-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Scale className="h-5 w-5 text-accent" />
                Balance Sheet
                <span className="text-sm font-normal text-muted-foreground ml-2">As on {reportDate}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  {renderGroupSection("Assets", balanceSheet.assets, "Total Assets", balanceSheet.totalAssets)}
                </div>
                <div>
                  {renderGroupSection("Liabilities", balanceSheet.liabilities, "Total Liabilities", balanceSheet.totalLiabilities)}
                  {renderGroupSection("Equity", balanceSheet.equity, "Capital", balanceSheet.totalEquity)}
                  <div className="flex justify-between py-2 px-3 bg-success/10 rounded-md text-sm font-bold text-success">
                    <span>Retained Earnings (P&L)</span>
                    <span>{formatCurrency(balanceSheet.retainedEarnings)}</span>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-border flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Assets = Liabilities + Equity</span>
                <span className={`text-sm font-bold ${Math.abs(balanceSheet.totalAssets - (balanceSheet.totalLiabilities + balanceSheet.totalEquity + balanceSheet.retainedEarnings)) < 1 ? "text-success" : "text-destructive"}`}>
                  {Math.abs(balanceSheet.totalAssets - (balanceSheet.totalLiabilities + balanceSheet.totalEquity + balanceSheet.retainedEarnings)) < 1 ? "✓ Balanced" : "⚠ Unbalanced"}
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PROFIT & LOSS */}
        <TabsContent value="profit-loss" className="mt-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-accent" />
                Profit & Loss Statement
                <span className="text-sm font-normal text-muted-foreground ml-2">Period ending {reportDate}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Revenue */}
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="bg-muted/50 px-4 py-2 text-xs font-bold text-muted-foreground uppercase tracking-wider">Revenue</div>
                <div className="divide-y divide-border">
                  <div className="flex justify-between px-4 py-3">
                    <span className="text-foreground">Sales Revenue</span>
                    <span className="font-semibold text-foreground">{formatCurrency(profitLoss.sales)}</span>
                  </div>
                </div>
              </div>

              {/* Cost of Goods Sold */}
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="bg-muted/50 px-4 py-2 text-xs font-bold text-muted-foreground uppercase tracking-wider">Cost of Goods Sold</div>
                <div className="divide-y divide-border">
                  <div className="flex justify-between px-4 py-3">
                    <span className="text-foreground">Purchases</span>
                    <span className="font-semibold text-foreground">{formatCurrency(profitLoss.purchases)}</span>
                  </div>
                  <div className="flex justify-between px-4 py-3">
                    <span className="text-foreground">Direct Expenses</span>
                    <span className="font-semibold text-foreground">{formatCurrency(profitLoss.directExpenses)}</span>
                  </div>
                </div>
              </div>

              {/* Gross Profit */}
              <div className="flex justify-between px-4 py-3 bg-accent/10 rounded-lg">
                <span className="font-bold text-accent">Gross Profit</span>
                <span className="font-bold text-accent text-lg">{formatCurrency(profitLoss.grossProfit)}</span>
              </div>

              {/* Operating Expenses */}
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="bg-muted/50 px-4 py-2 text-xs font-bold text-muted-foreground uppercase tracking-wider">Operating Expenses</div>
                <div className="divide-y divide-border">
                  <div className="flex justify-between px-4 py-3">
                    <span className="text-foreground">Indirect Expenses</span>
                    <span className="font-semibold text-foreground">{formatCurrency(profitLoss.indirectExpenses)}</span>
                  </div>
                </div>
              </div>

              {/* Other Income */}
              {profitLoss.otherIncome > 0 && (
                <div className="flex justify-between px-4 py-3 bg-success/10 rounded-lg">
                  <span className="font-medium text-success">Other Income</span>
                  <span className="font-semibold text-success">{formatCurrency(profitLoss.otherIncome)}</span>
                </div>
              )}

              {/* Net Profit */}
              <div className={`flex justify-between px-4 py-4 rounded-lg ${profitLoss.netProfit >= 0 ? "bg-success/20" : "bg-destructive/20"}`}>
                <span className={`font-bold text-lg ${profitLoss.netProfit >= 0 ? "text-success" : "text-destructive"}`}>
                  Net {profitLoss.netProfit >= 0 ? "Profit" : "Loss"}
                </span>
                <span className={`font-bold text-xl ${profitLoss.netProfit >= 0 ? "text-success" : "text-destructive"}`}>
                  {formatCurrency(Math.abs(profitLoss.netProfit))}
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TRIAL BALANCE */}
        <TabsContent value="trial-balance" className="mt-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-accent" />
                Trial Balance
                <span className="text-sm font-normal text-muted-foreground ml-2">As on {reportDate}</span>
                <span className={`ml-auto text-sm px-2 py-0.5 rounded ${trialBalance.isBalanced ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
                  {trialBalance.isBalanced ? "✓ Balanced" : "⚠ Unbalanced"}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Ledger</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Group</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Debit (₹)</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Credit (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trialBalance.rows.map((r, i) => (
                      <tr key={i} className="border-t border-border/50 hover:bg-muted/30">
                        <td className="py-2.5 px-4 font-medium text-foreground">{r.name}</td>
                        <td className="py-2.5 px-4 text-muted-foreground text-xs">{r.group}</td>
                        <td className="py-2.5 px-4 text-right font-medium">{r.debit > 0 ? formatCurrency(r.debit) : ""}</td>
                        <td className="py-2.5 px-4 text-right font-medium">{r.credit > 0 ? formatCurrency(r.credit) : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-accent/10 font-bold">
                      <td colSpan={2} className="py-3 px-4 text-accent">Total</td>
                      <td className="py-3 px-4 text-right text-accent">{formatCurrency(trialBalance.totalDebit)}</td>
                      <td className="py-3 px-4 text-right text-accent">{formatCurrency(trialBalance.totalCredit)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LEDGER REPORTS */}
        <TabsContent value="ledger" className="mt-6">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-accent" />
                  Ledger Account
                </CardTitle>
                <Select value={selectedLedger} onValueChange={setSelectedLedger}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Select ledger..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ledgers.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {!selectedLedger ? (
                <div className="text-center py-12 text-muted-foreground">
                  <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>Select a ledger to view transactions</p>
                </div>
              ) : ledgerReport && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground uppercase">Opening Balance</p>
                      <p className="text-lg font-bold text-foreground">{formatCurrency(ledgerReport.ledger.openingBalance)} {ledgerReport.ledger.openingBalanceType}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground uppercase">Current Balance</p>
                      <p className="text-lg font-bold text-accent">{formatCurrency(ledgerReport.ledger.currentBalance)} {ledgerReport.ledger.currentBalanceType}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground uppercase">Group</p>
                      <p className="text-lg font-bold text-foreground">{ledgerReport.ledger.group}</p>
                    </div>
                  </div>

                  <div className="rounded-lg border border-border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted/50">
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Date</th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Voucher</th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Narration</th>
                          <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Debit</th>
                          <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledgerReport.transactions.map((t, i) => (
                          <tr key={i} className="border-t border-border/50 hover:bg-muted/30">
                            <td className="py-2.5 px-4">{new Date(t.date).toLocaleDateString("en-IN")}</td>
                            <td className="py-2.5 px-4 font-medium text-[#2563EB] dark:text-blue-400 hover:text-blue-700 transition-colors">{t.voucherNumber}</td>
                            <td className="py-2.5 px-4 text-muted-foreground truncate max-w-xs">{t.narration}</td>
                            <td className="py-2.5 px-4 text-right font-medium">{t.debit > 0 ? formatCurrency(t.debit) : ""}</td>
                            <td className="py-2.5 px-4 text-right font-medium">{t.credit > 0 ? formatCurrency(t.credit) : ""}</td>
                          </tr>
                        ))}
                        {ledgerReport.transactions.length === 0 && (
                          <tr>
                            <td colSpan={5} className="py-8 text-center text-muted-foreground">No transactions found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CASH FLOW */}
        <TabsContent value="cash-flow" className="mt-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Wallet className="h-5 w-5 text-accent" />
                Cash Flow Statement
                <span className="text-sm font-normal text-muted-foreground ml-2">Period ending {reportDate}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground uppercase">Opening Balance</p>
                  <p className="text-2xl font-bold text-foreground">{formatCurrency(cashFlow.openingBalance)}</p>
                </div>
                <div className="p-4 rounded-lg bg-accent/10">
                  <p className="text-xs text-muted-foreground uppercase">Closing Balance</p>
                  <p className="text-2xl font-bold text-accent">{formatCurrency(cashFlow.currentBalance)}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between py-3 px-4 rounded-lg border border-border">
                  <span className="font-medium">Cash Flow from Operating Activities</span>
                  <span className={`font-bold ${cashFlow.operating >= 0 ? "text-success" : "text-destructive"}`}>{formatCurrency(cashFlow.operating)}</span>
                </div>
                <div className="flex justify-between py-3 px-4 rounded-lg border border-border">
                  <span className="font-medium">Cash Flow from Investing Activities</span>
                  <span className={`font-bold ${cashFlow.investing >= 0 ? "text-success" : "text-destructive"}`}>{formatCurrency(cashFlow.investing)}</span>
                </div>
                <div className="flex justify-between py-3 px-4 rounded-lg border border-border">
                  <span className="font-medium">Cash Flow from Financing Activities</span>
                  <span className={`font-bold ${cashFlow.financing >= 0 ? "text-success" : "text-destructive"}`}>{formatCurrency(cashFlow.financing)}</span>
                </div>
              </div>

              <div className={`flex justify-between py-4 px-4 rounded-lg ${cashFlow.netChange >= 0 ? "bg-success/20" : "bg-destructive/20"}`}>
                <span className="font-bold text-lg">Net Change in Cash</span>
                <span className={`font-bold text-xl ${cashFlow.netChange >= 0 ? "text-success" : "text-destructive"}`}>{formatCurrency(cashFlow.netChange)}</span>
              </div>

              <div className="pt-4 border-t border-border">
                <h4 className="text-sm font-bold text-muted-foreground uppercase mb-3">Cash & Bank Accounts</h4>
                <div className="space-y-2">
                  {cashFlow.ledgers.map((l) => (
                    <div key={l.id} className="flex justify-between py-2 px-3 bg-muted/30 rounded">
                      <span>{l.name}</span>
                      <span className="font-semibold">{formatCurrency(l.currentBalance)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* INVENTORY */}
        <TabsContent value="inventory" className="mt-6">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5 text-accent" />
                  Inventory Valuation Report
                  <span className="text-sm font-normal text-muted-foreground ml-2">As on {reportDate}</span>
                </CardTitle>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">{inventoryReport.totalItems} items</span>
                  <span className="font-bold text-accent">{formatCurrency(inventoryReport.totalValue)}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Item</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">HSN</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Group</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Qty</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Rate</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Value</th>
                      <th className="text-center py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryReport.items.map((item) => (
                      <tr key={item.id} className="border-t border-border/50 hover:bg-muted/30">
                        <td className="py-2.5 px-4 font-medium text-foreground">{item.name}</td>
                        <td className="py-2.5 px-4 text-muted-foreground">{item.hsnCode}</td>
                        <td className="py-2.5 px-4 text-muted-foreground text-xs">{item.stockGroup}</td>
                        <td className="py-2.5 px-4 text-right">{item.currentQty} {item.unit}</td>
                        <td className="py-2.5 px-4 text-right">{formatCurrency(item.currentRate)}</td>
                        <td className="py-2.5 px-4 text-right font-semibold">{formatCurrency(item.currentValue)}</td>
                        <td className="py-2.5 px-4 text-center">
                          {item.reorderLevel && item.currentQty <= item.reorderLevel ? (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-destructive/10 text-destructive">Low Stock</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-success/10 text-success">OK</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-accent/10 font-bold">
                      <td colSpan={5} className="py-3 px-4 text-accent">Total Inventory Value</td>
                      <td className="py-3 px-4 text-right text-accent">{formatCurrency(inventoryReport.totalValue)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* GST SUMMARY */}
        <TabsContent value="gst" className="mt-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Receipt className="h-5 w-5 text-accent" />
                GST Summary Report
                <span className="text-sm font-normal text-muted-foreground ml-2">Period ending {reportDate}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground uppercase">Taxable Sales</p>
                  <p className="text-xl font-bold text-foreground">{formatCurrency(gstSummary.taxableSupply)}</p>
                  <p className="text-xs text-muted-foreground mt-1">{gstSummary.salesCount} invoices</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground uppercase">Taxable Purchases</p>
                  <p className="text-xl font-bold text-foreground">{formatCurrency(gstSummary.taxablePurchase)}</p>
                  <p className="text-xs text-muted-foreground mt-1">{gstSummary.purchaseCount} invoices</p>
                </div>
                <div className={`p-4 rounded-lg ${gstSummary.netPayable >= 0 ? "bg-destructive/10" : "bg-success/10"}`}>
                  <p className="text-xs text-muted-foreground uppercase">{gstSummary.netPayable >= 0 ? "Net Tax Payable" : "Net Refund"}</p>
                  <p className={`text-xl font-bold ${gstSummary.netPayable >= 0 ? "text-destructive" : "text-success"}`}>{formatCurrency(Math.abs(gstSummary.netPayable))}</p>
                </div>
              </div>

              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Tax Type</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">CGST (₹)</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">SGST (₹)</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">IGST (₹)</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Total (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-border/50">
                      <td className="py-3 px-4 font-medium text-foreground">Output Tax (Sales)</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(gstSummary.outputCGST)}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(gstSummary.outputSGST)}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(gstSummary.outputIGST)}</td>
                      <td className="py-3 px-4 text-right font-semibold">{formatCurrency(gstSummary.totalOutput)}</td>
                    </tr>
                    <tr className="border-t border-border/50">
                      <td className="py-3 px-4 font-medium text-foreground">Input Tax Credit (Purchases)</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(gstSummary.inputCGST)}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(gstSummary.inputSGST)}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(gstSummary.inputIGST)}</td>
                      <td className="py-3 px-4 text-right font-semibold">{formatCurrency(gstSummary.totalInput)}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr className={`font-bold ${gstSummary.netPayable >= 0 ? "bg-destructive/10" : "bg-success/10"}`}>
                      <td colSpan={4} className="py-3 px-4">{gstSummary.netPayable >= 0 ? "Net Tax Payable" : "Net Tax Refundable"}</td>
                      <td className={`py-3 px-4 text-right text-lg ${gstSummary.netPayable >= 0 ? "text-destructive" : "text-success"}`}>{formatCurrency(Math.abs(gstSummary.netPayable))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        {/* DAY BOOK */}
        <TabsContent value="day-book" className="mt-6">
          <Card>
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Day Book
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  {fromDate} to {toDate}
                </span>
              </CardTitle>
              <span className="text-xs font-mono text-muted-foreground">
                {dayBookVouchers.length} transactions
              </span>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50 text-muted-foreground font-semibold uppercase text-[10px]">
                      <th className="py-2.5 px-3 text-left">Date</th>
                      <th className="py-2.5 px-3 text-left">Voucher #</th>
                      <th className="py-2.5 px-3 text-left">Type</th>
                      <th className="py-2.5 px-3 text-left">Party / Particulars</th>
                      <th className="py-2.5 px-3 text-left">Mode / Ref</th>
                      <th className="py-2.5 px-3 text-right">Debit (₹)</th>
                      <th className="py-2.5 px-3 text-right">Credit (₹)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {dayBookVouchers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-muted-foreground">
                          No transactions found for the selected period.
                        </td>
                      </tr>
                    ) : (
                      dayBookVouchers.map((v) => (
                        <tr key={v.id} className="hover:bg-muted/30">
                          <td className="py-2 px-3 font-mono text-muted-foreground whitespace-nowrap">{v.date}</td>
                          <td className="py-2 px-3 font-mono font-medium text-foreground">{v.voucherNumber}</td>
                          <td className="py-2 px-3">
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/10 text-primary">
                              {v.type}
                            </span>
                          </td>
                          <td className="py-2 px-3 max-w-[240px] truncate">
                            <span className="font-medium text-foreground">{v.partyName || v.narration || "General Entry"}</span>
                          </td>
                          <td className="py-2 px-3 text-muted-foreground font-mono text-[11px]">
                            {v.instrumentNo || v.paymentMode || "—"}
                          </td>
                          <td className="py-2 px-3 text-right font-mono font-medium text-foreground">
                            {formatCurrency(v.totalDebit)}
                          </td>
                          <td className="py-2 px-3 text-right font-mono font-medium text-foreground">
                            {formatCurrency(v.totalCredit)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {dayBookVouchers.length > 0 && (
                    <tfoot className="bg-muted/40 font-bold border-t border-border">
                      <tr>
                        <td colSpan={5} className="py-2.5 px-3 text-foreground text-xs">Total Day Book Volume</td>
                        <td className="py-2.5 px-3 text-right font-mono text-foreground text-xs">
                          {formatCurrency(dayBookVouchers.reduce((s, v) => s + v.totalDebit, 0))}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-foreground text-xs">
                          {formatCurrency(dayBookVouchers.reduce((s, v) => s + v.totalCredit, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CASH BOOK */}
        <TabsContent value="cash-book" className="mt-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Wallet className="h-5 w-5 text-accent" />
                Cash Book
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  {fromDate} to {toDate}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg bg-muted/40 border border-border">
                  <span className="text-[10px] text-muted-foreground uppercase block">Opening Balance</span>
                  <span className="text-sm sm:text-base font-bold font-mono text-foreground">
                    {formatCurrency(cashBookData.openingBalance)}
                  </span>
                </div>
                <div className="p-3 rounded-lg bg-success/10 border border-success/20">
                  <span className="text-[10px] text-muted-foreground uppercase block">Total Cash Received (Dr)</span>
                  <span className="text-sm sm:text-base font-bold font-mono text-success">
                    {formatCurrency(cashBookData.totalDebit)}
                  </span>
                </div>
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <span className="text-[10px] text-muted-foreground uppercase block">Total Cash Paid (Cr)</span>
                  <span className="text-sm sm:text-base font-bold font-mono text-destructive">
                    {formatCurrency(cashBookData.totalCredit)}
                  </span>
                </div>
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                  <span className="text-[10px] text-muted-foreground uppercase block">Closing Balance</span>
                  <span className="text-sm sm:text-base font-bold font-mono text-primary">
                    {formatCurrency(cashBookData.closingBalance)}
                  </span>
                </div>
              </div>

              {/* Cash Ledger Details */}
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50 text-muted-foreground font-semibold uppercase text-[10px]">
                      <th className="py-2.5 px-3 text-left">Date</th>
                      <th className="py-2.5 px-3 text-left">Voucher #</th>
                      <th className="py-2.5 px-3 text-left">Type</th>
                      <th className="py-2.5 px-3 text-left">Particulars</th>
                      <th className="py-2.5 px-3 text-right">Receipt / Inflow (Dr)</th>
                      <th className="py-2.5 px-3 text-right">Payment / Outflow (Cr)</th>
                      <th className="py-2.5 px-3 text-right">Running Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border font-mono">
                    {cashBookData.entries.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-muted-foreground font-sans">
                          No cash transactions recorded in this period.
                        </td>
                      </tr>
                    ) : (
                      cashBookData.entries.map((e, idx) => (
                        <tr key={idx} className="hover:bg-muted/30">
                          <td className="py-2 px-3 text-muted-foreground">{e.date}</td>
                          <td className="py-2 px-3 font-medium text-foreground">{e.voucherNumber}</td>
                          <td className="py-2 px-3">
                            <span className="px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground font-sans">
                              {e.type}
                            </span>
                          </td>
                          <td className="py-2 px-3 max-w-[220px] truncate font-sans text-foreground">
                            {e.particulars}
                          </td>
                          <td className="py-2 px-3 text-right text-success">
                            {e.debit > 0 ? formatCurrency(e.debit) : "—"}
                          </td>
                          <td className="py-2 px-3 text-right text-destructive">
                            {e.credit > 0 ? formatCurrency(e.credit) : "—"}
                          </td>
                          <td className="py-2 px-3 text-right font-bold text-foreground">
                            {formatCurrency(e.runningBalance)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* BANK BOOK */}
        <TabsContent value="bank-book" className="mt-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Landmark className="h-5 w-5 text-primary" />
                Bank Book
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  {fromDate} to {toDate}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg bg-muted/40 border border-border">
                  <span className="text-[10px] text-muted-foreground uppercase block">Opening Balance</span>
                  <span className="text-sm sm:text-base font-bold font-mono text-foreground">
                    {formatCurrency(bankBookData.openingBalance)}
                  </span>
                </div>
                <div className="p-3 rounded-lg bg-success/10 border border-success/20">
                  <span className="text-[10px] text-muted-foreground uppercase block">Total Deposits (Dr)</span>
                  <span className="text-sm sm:text-base font-bold font-mono text-success">
                    {formatCurrency(bankBookData.totalDebit)}
                  </span>
                </div>
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <span className="text-[10px] text-muted-foreground uppercase block">Total Withdrawals (Cr)</span>
                  <span className="text-sm sm:text-base font-bold font-mono text-destructive">
                    {formatCurrency(bankBookData.totalCredit)}
                  </span>
                </div>
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                  <span className="text-[10px] text-muted-foreground uppercase block">Closing Balance</span>
                  <span className="text-sm sm:text-base font-bold font-mono text-primary">
                    {formatCurrency(bankBookData.closingBalance)}
                  </span>
                </div>
              </div>

              {/* Bank Ledger Details */}
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50 text-muted-foreground font-semibold uppercase text-[10px]">
                      <th className="py-2.5 px-3 text-left">Date</th>
                      <th className="py-2.5 px-3 text-left">Voucher #</th>
                      <th className="py-2.5 px-3 text-left">Type</th>
                      <th className="py-2.5 px-3 text-left">Particulars</th>
                      <th className="py-2.5 px-3 text-left">Instrument / Ref</th>
                      <th className="py-2.5 px-3 text-right">Deposit (Dr)</th>
                      <th className="py-2.5 px-3 text-right">Withdrawal (Cr)</th>
                      <th className="py-2.5 px-3 text-right">Running Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border font-mono">
                    {bankBookData.entries.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-muted-foreground font-sans">
                          No bank transactions recorded in this period.
                        </td>
                      </tr>
                    ) : (
                      bankBookData.entries.map((e, idx) => (
                        <tr key={idx} className="hover:bg-muted/30">
                          <td className="py-2 px-3 text-muted-foreground">{e.date}</td>
                          <td className="py-2 px-3 font-medium text-foreground">{e.voucherNumber}</td>
                          <td className="py-2 px-3">
                            <span className="px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground font-sans">
                              {e.type}
                            </span>
                          </td>
                          <td className="py-2 px-3 max-w-[200px] truncate font-sans text-foreground">
                            {e.particulars}
                          </td>
                          <td className="py-2 px-3 text-muted-foreground text-[11px]">
                            {e.instrumentRef || "—"}
                          </td>
                          <td className="py-2 px-3 text-right text-success">
                            {e.debit > 0 ? formatCurrency(e.debit) : "—"}
                          </td>
                          <td className="py-2 px-3 text-right text-destructive">
                            {e.credit > 0 ? formatCurrency(e.credit) : "—"}
                          </td>
                          <td className="py-2 px-3 text-right font-bold text-foreground">
                            {formatCurrency(e.runningBalance)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
